import { Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { capitalize } from '@/lib/utils'

function groupBySection(schema) {
  const groups = []
  let current = null
  for (const field of schema || []) {
    const sectionName = field.section || 'Intake'
    if (!current || current.name !== sectionName) {
      current = { name: sectionName, fields: [] }
      groups.push(current)
    }
    current.fields.push(field)
  }
  return groups
}

function formatValue(field, value) {
  if (value == null || value === '') return '—'
  if (field.type === 'date') return formatDate(value)
  if (typeof value === 'string') return capitalize(value)
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

export default function Patient({ currentUser, patient, intakeSchema = [] }) {
  const fullName = [capitalize(patient.first_name), capitalize(patient.last_name)].filter(Boolean).join(' ') || 'Unnamed patient'

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <Link href="/dashboard" className="text-xl font-bold">Live Triage</Link>
          <div className="flex items-center gap-4">
            <Link href="/dashboard">
              <Button variant="outline" size="sm">Back to dashboard</Button>
            </Link>
            <span className="text-sm text-muted-foreground">{currentUser?.email}</span>
          </div>
        </div>
      </header>

      <main className="container mx-auto grid gap-8 px-4 py-10 lg:grid-cols-[minmax(0,1fr)_420px]">
        <section className="space-y-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{fullName}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Triage recorded {formatDateTime(patient.created_at)}
            </p>
          </div>

          <RecordingPlayer url={patient.recording_url} />
        </section>

        <aside className="space-y-4">
          <section className={`rounded-lg border p-5 ${patient.urgent ? 'border-red-600/40 bg-red-50' : 'bg-card'}`}>
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold tracking-tight">Estimated ESI</h2>
              <EsiBadge score={patient.estimated_esi_score} urgent={patient.urgent} />
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              {patient.esi_reason || 'No reason recorded.'}
            </p>
          </section>

          <section className="rounded-lg border bg-card">
            <div className="border-b px-5 py-3">
              <h2 className="text-sm font-semibold tracking-tight">Patient intake</h2>
            </div>
            <div className="divide-y">
              {groupBySection(intakeSchema).map((section) => (
                <div key={section.name} className="px-5 py-4">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {section.name}
                  </p>
                  <dl className="space-y-2">
                    {section.fields.map((field) => {
                      const value = patient.intake_data?.[field.key]
                      return (
                        <div key={field.key} className="grid grid-cols-[140px_1fr] items-start gap-3">
                          <dt className="pt-0.5 text-xs text-muted-foreground">{field.label}</dt>
                          <dd className={`text-sm ${value ? 'text-foreground' : 'text-muted-foreground/40'}`}>
                            {formatValue(field, value)}
                          </dd>
                        </div>
                      )
                    })}
                  </dl>
                </div>
              ))}
            </div>
          </section>
        </aside>
      </main>
    </div>
  )
}

function RecordingPlayer({ url }) {
  if (!url) {
    return (
      <section className="rounded-lg border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">No recording available for this triage call.</p>
      </section>
    )
  }
  return (
    <section className="overflow-hidden rounded-lg border bg-black">
      <video
        src={url}
        controls
        playsInline
        className="aspect-video w-full bg-black"
      />
    </section>
  )
}

function EsiBadge({ score, urgent }) {
  if (score == null) {
    return <span className="text-xs text-muted-foreground">Pending</span>
  }
  const className = urgent
    ? 'inline-flex h-6 items-center rounded-md border border-red-600/30 bg-red-600 px-2 text-xs font-semibold text-white'
    : 'inline-flex h-6 items-center rounded-md border bg-muted px-2 text-xs font-medium'
  return <span className={className}>ESI {score}</span>
}

function formatDate(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

function formatDateTime(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}
